/* eslint-disable react/jsx-no-undef */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, functions } from "../../../config/firebaseConfig";
import {
    doc,
    getDoc,
    collection,
    getDocs,
    orderBy,
    query,
    limit,
    collectionGroup,
    where,
} from "firebase/firestore";
import remarkGfm from "remark-gfm";
import { Button, Dialog } from "@headlessui/react";
import { ArrowUturnLeftIcon, ChevronLeftIcon, PencilIcon } from "@heroicons/react/16/solid";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import ReactMarkdown from "react-markdown";
import { useToast } from "../../../util/ToastProvider";
import { httpsCallable } from "firebase/functions";

export default function LayerPreview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const templateManagerHandler = httpsCallable(functions, "templateManager");
    const toaster = useToast();

    const [layer, setLayer] = useState<any>(null);
    const [versions, setVersions] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const { user } = useSelector((s: RootState) => s.auth);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [usedOpen, setUsedOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    async function handleDelete() {
        try {
          setDeleting(true);
    
          const res = await templateManagerHandler({
            action: "deleteLayerOrAnnexure",
            type: "layer",
            id: layer.id
          });
    
          console.log("Deleted:", res);
    
          // Close dialog
          setDeleteOpen(false);
          toaster.success("Layer deleted successfully");
          // Navigate back to list
          navigate("/admin/layers");
        } catch (err:any) {
          console.error(err);
          toaster.error(err.message || "Failed to delete");
        } finally {
          setDeleting(false);
        }
      }

    // new: keep track of which templates use this layer
    const [usedByTemplates, setUsedByTemplates] = useState<any[]>([]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            const ref = doc(db, "layers", "meta", "data", id);
            const snap = await getDoc(ref);
            if (snap.exists()) setLayer({ id: snap.id, ...snap.data() });

            const vq = query(
                collection(ref, "versions"),
                orderBy("createdAt", "desc"),
                limit(2)
            );
            const vdocs = await getDocs(vq);
            setVersions(vdocs.docs.map((d) => ({ id: d.id, ...d.data() })));
        })();
    }, [id]);

    // 🔎 new effect: find templates that use this layer
    useEffect(() => {
        if (!id) return;
        let mounted = true;

        (async () => {
            try {
                const q = query(
                    collectionGroup(db, "versions"),
                    where("layerIds", "array-contains", id)
                );
                const snaps = await getDocs(q);

                const results: any[] = [];
                for (const s of snaps.docs) {
                    const versionId = s.id; // "draft" or "published"
                    const templateDocRef = s.ref.parent.parent; // /templates/meta/data/{templateId}
                    const templateId = templateDocRef ? templateDocRef.id : "unknown";

                    results.push({
                        templateId,
                        versionId,
                        title: s.get("title") ?? null,
                        updatedAt: s.get("updatedAt") ?? null,
                    });
                }

                if (mounted) {
                    setUsedByTemplates(results);
                    console.log("Templates using this layer:", results);
                }
            } catch (err) {
                console.error("Error fetching templates for layer:", err);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id]);

    return (
        layer && (
            <div className="mx-auto max-w-5xl px-6 py-6">
                {/* Top actions */}
                <div className="mt-4 ">
                    <div>
                        <button
                            onClick={() => navigate("/admin/layers")}
                            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                            Layers
                        </button>
                    </div>


                </div>

                {/* Title */}
                <div className="flex justify-between items-center">
                    <h1 className="mt-4 text-2xl font-semibold ">
                        {layer.layerNo}: {layer.title}
                    </h1>
                    <button
                        onClick={() => {
                            if (usedByTemplates.filter(t => t.versionId === "published").length > 0) {
                            setUsedOpen(true);
                            } else {
                            setDeleteOpen(true);
                            }
                        }}
                        className="px-4 py-2 border border-[2px] !border-dark-border-danger !text-dark-border-danger rounded-lg"
                        >
                        Delete
                        </button>
                </div>


                <div className="mt-8 flex justify-between items-center">
                    <div >
                        <div className="text-sm font-medium text-zinc-700 mb-2">Connected Reports</div>

                        <div className="flex flex-wrap gap-2">
                            {usedByTemplates && usedByTemplates.length > 0 ? (
                                // filter to published only, then map
                                usedByTemplates
                                    .filter((t: any) => t.versionId === "published")
                                    .map((t: any) => (
                                        <span
                                            key={`${t.templateId}-${t.versionId}`}
                                            className="px-3 py-1 text-xs rounded-full bg-gray-200 text-gray-700 truncate max-w-[20ch]"

                                        >
                                            {t.title ?? t.templateId}
                                        </span>
                                    ))
                            ) : (
                                <div className="text-sm text-zinc-400">No connected published reports</div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-4 items-center">
                        <button
                            onClick={() => setOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-gray-200 px-4 py-2 text-black hover:bg-gray-300"
                        >
                            See previous versions
                        </button>
                        {(user?.role === "admin" || user?.role === "approver") && (
                            <button
                                onClick={() => navigate(`/admin/layers/${id}`)}
                                className="flex gap-1 items-center rounded-lg bg-brand-600 px-4 py-2 text-white hover:bg-brand-600/90"
                            >
                                <PencilIcon className="h-4 w-4" aria-hidden="true" />
                                Edit
                            </button>
                        )}
                    </div>
                </div>





                {/* Content */}
                {/* <div className="mt-6 whitespace-pre-line rounded-xl border p-4 bg-white shadow">
                    
                </div> */}
                <div className="border mt-4 p-6 rounded-xl">
                    <div className="prose max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {(layer.content || "").replace(/\\n/g, "\n")}
                        </ReactMarkdown>
                    </div>
                </div>

                <Dialog open={open} onClose={()=> {}}  className="relative z-50">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-5xl rounded-xl bg-white shadow-xl">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b px-6 py-4">
                                <Dialog.Title className="text-lg font-medium text-zinc-600">
                                    Previous versions
                                </Dialog.Title>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Body */}
                            <div className="min-h-[550px] max-h-[70vh] overflow-y-auto px-6 py-5">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    {/* Left: current */}
                                    <section>
                                        <div className="mb-3 flex items-center justify-between">
                                            <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700">
                                                Current version
                                            </span>
                                            {(user?.role === "admin" || user?.role === "approver") && (
                                                <button
                                                    onClick={() => navigate(`/admin/layers/${id}`)}
                                                    className=" flex gap-1 items-center rounded-md bg-zinc-200 px-3 py-1.5 text-sm text-black hover:bg-zinc-300"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                    Edit
                                                </button>
                                            )}
                                        </div>

                                        <div className="prose rounded-lg border bg-zinc-50 p-4 text-sm text-zinc-700">

                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {(layer.content || "").replace(/\\n/g, "\n")}
                                            </ReactMarkdown>
                                        </div>
                                    </section>

                                    {/* Right: previous (n-1) */}
                                    <section >
                                        {versions.length === 0 ? (
                                            <div className="mt-[55px]  min-h-[12rem] rounded-lg border bg-zinc-50 p-4 text-sm text-zinc-400">
                                                No previous versions
                                            </div>
                                        ) : (
                                            versions.map((v, i) => (
                                                <div key={v.id} className={i ? "mt-6" : ""}>
                                                    <div className="mb-3 inline-flex items-center gap-3">
                                                        <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700">
                                                            {v.createdAt?.seconds
                                                                ? `Previous version: ${new Date(v.createdAt.seconds * 1000).toLocaleString()}`
                                                                : "Previous version"}
                                                        </span>
                                                        <button
                                                            onClick={() => console.log("Revert to", v.id)}
                                                            className="inline-flex items-center gap-1 rounded-md bg-zinc-200 px-3 py-1.5 text-sm text-black hover:bg-zinc-300"
                                                        >
                                                            <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                                                            Revert to this version
                                                        </button>
                                                    </div>

                                                    <div className="prose rounded-lg border bg-zinc-50 p-4 text-sm text-zinc-700">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                            {(v.content || "").replace(/\\n/g, "\n")}
                                                        </ReactMarkdown>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </section>
                                </div>
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>

                <Dialog open={usedOpen} onClose={()=> {}} className="relative z-50">
  <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

  <div className="fixed inset-0 flex items-center justify-center p-4">
    <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
      <Dialog.Title className="text-lg font-semibold text-zinc-800">
        Layer is used by a template
      </Dialog.Title>

      <p className="mt-3 text-sm text-zinc-600">
        This layer is used by template(s), therefore it cannot be deleted.
        First, remove this layer from template(s), then delete it.
      </p>

      <div className="mt-6 flex justify-end">
        <button
          onClick={() => setUsedOpen(false)}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-600/90"
        >
          Close
        </button>
      </div>
    </Dialog.Panel>
  </div>
                </Dialog>

                <Dialog open={deleteOpen} onClose={()=> {}}  className="relative z-50">
                    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                        <Dialog.Title className="text-lg font-semibold text-zinc-800">
                            Delete layer?
                        </Dialog.Title>

                        <p className="mt-3 text-sm text-zinc-600">
                            Are you certain you want to delete this layer? This action cannot be reversed.
                        </p>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                            disabled={deleting}
                            onClick={() => setDeleteOpen(false)}
                            className="rounded-md bg-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-300"
                            >
                            No, go back
                            </button>

                            <button
                            disabled={deleting}
                            onClick={() => {
                                console.log("Delete layer:", layer.id);
                                handleDelete()
                            }}
                            className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700"
                            >
                            {!deleting ? "Yes, delete" : "Deleteing"}
                            </button>
                        </div>
                        </Dialog.Panel>
                    </div>
                    </Dialog>


            </div>
        )
    );
}
